import { useIntl } from "react-intl";
import "./RoomAllocationEditor.scss";
import { IoAddCircleOutline, IoCloseCircleOutline } from "react-icons/io5";

const MAX_ROOMS = 4;
const MAX_ADULTS_PER_ROOM = 6;
const MAX_CHILDREN_PER_ROOM = 4;
const CHILD_AGE_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17];

const emptyRoom = () => ({ adults: 2, childrenAges: [] });

export default function RoomAllocationEditor({ rooms, onChange, roomTypeOptions, boardTypeOptions }) {
  const intl = useIntl();
  const list = Array.isArray(rooms) && rooms.length > 0 ? rooms : [emptyRoom()];
  const typeOptions = Array.isArray(roomTypeOptions) ? roomTypeOptions : null;
  const boardOptions = Array.isArray(boardTypeOptions) ? boardTypeOptions : null;

  const updateRoom = (idx, patch) => {
    onChange(list.map((room, i) => (i === idx ? { ...room, ...patch } : room)));
  };

  const setAdults = (idx, adults) => updateRoom(idx, { adults: Math.min(MAX_ADULTS_PER_ROOM, Math.max(1, adults)) });

  const setChildCount = (idx, count) => {
    const clamped = Math.min(MAX_CHILDREN_PER_ROOM, Math.max(0, count));
    const current = list[idx].childrenAges || [];
    const next = current.slice(0, clamped);
    while (next.length < clamped) next.push(0);
    updateRoom(idx, { childrenAges: next });
  };

  const setChildAge = (idx, childIdx, age) => {
    const next = [...(list[idx].childrenAges || [])];
    next[childIdx] = Number(age) || 0;
    updateRoom(idx, { childrenAges: next });
  };

  const addRoom = () => {
    if (list.length >= MAX_ROOMS) return;
    onChange([...list, emptyRoom()]);
  };

  const removeRoom = (idx) => {
    if (list.length <= 1) return;
    onChange(list.filter((_, i) => i !== idx));
  };

  return (
    <div className="room_allocation_editor">
      {list.map((room, idx) => (
        <div className="room_allocation_editor-room" key={idx}>
          <div className="room_allocation_editor-room_head">
            <span className="room_allocation_editor-room_title">
              {intl.formatMessage({ id: "roomLabel" })} {idx + 1}
            </span>
            {list.length > 1 && (
              <button type="button" className="room_allocation_editor-room_remove" onClick={() => removeRoom(idx)}>
                <IoCloseCircleOutline />
              </button>
            )}
          </div>

          <div className="room_allocation_editor-row">
            <span className="room_allocation_editor-label">{intl.formatMessage({ id: "adults" })}</span>
            <div className="room_allocation_editor-counter">
              <button type="button" onClick={() => setAdults(idx, room.adults - 1)} disabled={room.adults <= 1}>
                −
              </button>
              <span>{room.adults}</span>
              <button type="button" onClick={() => setAdults(idx, room.adults + 1)} disabled={room.adults >= MAX_ADULTS_PER_ROOM}>
                +
              </button>
            </div>
          </div>

          <div className="room_allocation_editor-row">
            <span className="room_allocation_editor-label">{intl.formatMessage({ id: "children" })}</span>
            <div className="room_allocation_editor-counter">
              <button type="button" onClick={() => setChildCount(idx, room.childrenAges.length - 1)} disabled={room.childrenAges.length <= 0}>
                −
              </button>
              <span>{room.childrenAges.length}</span>
              <button type="button" onClick={() => setChildCount(idx, room.childrenAges.length + 1)} disabled={room.childrenAges.length >= MAX_CHILDREN_PER_ROOM}>
                +
              </button>
            </div>
          </div>

          {typeOptions && (
            <div className="room_allocation_editor-row">
              <span className="room_allocation_editor-label">{intl.formatMessage({ id: "roomTypeLabel" })}</span>
              <select
                className="room_allocation_editor-typeselect"
                value={room.roomType || ""}
                onChange={(e) => updateRoom(idx, { roomType: e.target.value || undefined })}
              >
                <option value="">{intl.formatMessage({ id: "roomTypeAny" })}</option>
                {typeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {boardOptions && (
            <div className="room_allocation_editor-row">
              <span className="room_allocation_editor-label">{intl.formatMessage({ id: "boardTypeLabel" })}</span>
              <select
                className="room_allocation_editor-typeselect"
                value={room.boardType || ""}
                onChange={(e) => updateRoom(idx, { boardType: e.target.value || undefined })}
              >
                <option value="">{intl.formatMessage({ id: "roomTypeAny" })}</option>
                {boardOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {room.childrenAges.length > 0 && (
            <div className="room_allocation_editor-ages">
              {room.childrenAges.map((age, childIdx) => (
                <div className="room_allocation_editor-age" key={childIdx}>
                  <label>
                    {intl.formatMessage({ id: "child" })} {childIdx + 1}
                  </label>
                  <select value={age} onChange={(e) => setChildAge(idx, childIdx, e.target.value)}>
                    <option value={0} disabled>
                      {intl.formatMessage({ id: "pleaseSelect" })}
                    </option>
                    {CHILD_AGE_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt === 1 ? "<2" : opt} {intl.formatMessage({ id: "years" })}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      {list.length < MAX_ROOMS && (
        <button type="button" className="room_allocation_editor-add" onClick={addRoom}>
          <IoAddCircleOutline /> {intl.formatMessage({ id: "addRoom" })}
        </button>
      )}
    </div>
  );
}
